<script lang="ts">
export default {
  inheritAttrs: false,
};

type Size = "sm" | "md" | "lg" | "xl";
</script>

<script setup lang="ts">
import _ from "lodash";
import { twMerge } from "tailwind-merge";
import { Dialog as HeadlessDialog, TransitionRoot } from "@headlessui/vue";
import { provide, useAttrs, computed, ref, Ref, watch } from "vue";

export type ProvideDialog = {
  open: boolean;
  zoom: Ref<boolean>;
  size?: Size;
};

interface DialogProps extends ExtractProps<typeof HeadlessDialog> {
  size?: Size;
  open: boolean;
  staticBackdrop?: boolean;
  needToMove?: boolean;
}

//@ts-ignore
const props = withDefaults(defineProps<DialogProps>(), {
  as: "div",
  open: false,
  size: "md",
});

// eslint-disable-next-line
const { as, onClose, staticBackdrop, size } = props;

function openModal() {
  if(props.needToMove) {
    const ids = [
      // @ts-ignore
      '#' + process.env.VUE_APP_PUBLISHED_NAME,
      '#headlessui-portal-root',
    ]

    // Wait for all container to be loaded, then mode Dialog
    ids.forEach((id: string, index: number) => {
      waitForElement(id, 5000).then(function(){
        // console.log('%c %s loaded', 'background: #222; color: lightgreen', id);
        if(ids.length === index + 1) {
          // console.log('%c all apps loaded ', 'background: #222; color: lightgreen');

          const rightDiv = document.getElementById("headlessui-portal-root");

          // @ts-ignore
          const rightPlace = document.getElementById(process.env.VUE_APP_PUBLISHED_NAME);

          if(rightDiv != null && rightPlace != null) {
            rightPlace?.appendChild(rightDiv);
          }

          const mainElement = document.getElementById("management-main_3125750820360949067");

          if(mainElement) {
            mainElement.removeAttribute("inert");
          }
        }
      }).catch(()=>{
        console.log('%c subapp %s did not load', 'background: #222; color: red', id);
      });
    })
  }
}

const open = computed(() => props.open);

watch(computed(() => props.open), function() {
  if(open.value) {
    openModal();
  }
  }
);

const attrs = useAttrs();
const computedClass = computed(() =>
  twMerge(["relative z-[60]", typeof attrs.class === "string" && attrs.class])
);

const zoom = ref(false);

// @ts-ignore
const emit = defineEmits<{
  (e: "close", value: boolean): void;
}>();

const handleClose = (value: boolean) => {
  if (!staticBackdrop) {
    onClose && onClose(value);
    emit("close", value);
  } else {
    zoom.value = true;
    setTimeout(() => {
      zoom.value = false;
    }, 300);
  }
};

/**
 * Wait for an element before resolving a promise
 * @param {String} querySelector - Selector of element to wait for
 * @param {Integer} timeout - Milliseconds to wait before timing out, or 0 for no timeout
 */
function waitForElement(querySelector: string, timeout: number) {
  return new Promise<void>((resolve, reject)=>{
    let timer: any = false;

    if(document.querySelectorAll(querySelector).length) {
      return resolve();
    }

    const observer = new MutationObserver(()=>{
      if(document.querySelectorAll(querySelector).length){
        observer.disconnect();
        if(timer !== false) {
          clearTimeout(timer);
        }

        return resolve();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    if(timeout) {
      timer = setTimeout(()=>{
        observer.disconnect();
        reject();
      }, timeout);
    }
  });
}

// @ts-ignore
provide<ProvideDialog>("dialog", {
  open: open.value,
  zoom: zoom,
  size: size,
});
</script>

<template>
  <TransitionRoot appear as="template" :show="open">
    <HeadlessDialog
      :as="as"
      @close="
        (value) => {
          handleClose(value);
        }
      "
      :class="computedClass"
      v-bind="_.omit(attrs, 'class', 'onClose')"
    >
      <slot></slot>
    </HeadlessDialog>
  </TransitionRoot>
</template>
